import {
  addSentryBreadcrumb,
  amplitudeFlush,
  amplitudeTrackEvent,
  captureSentryMessage,
} from '@airship-platform/airship-patterns';
import { distinctUntilChanged, filter, map } from 'rxjs';
import {
  type ResetSource,
  type RxDbConnectionService,
  type RxDbPhase,
  type SchemaRebuildProblem,
} from './rxDbConnectionService';

const phaseDetail = (phase: RxDbPhase): Record<string, unknown> | undefined => {
  switch (phase.status) {
    case 'resetting':
      return phase.trigger === 'manual-destructive'
        ? { trigger: phase.trigger, source: phase.source }
        : { trigger: phase.trigger };
    case 'unavailable':
      return { reason: phase.reason.kind };
    default:
      return undefined;
  }
};

const tabCountLabel = (otherTabCount: number | null): string =>
  otherTabCount === null ? 'unknown' : String(otherTabCount);

const trackRxDbUnavailable = (
  reasonKind: string,
  otherTabCount: number | null
) => {
  amplitudeTrackEvent('RxDB Unavailable', {
    reason: reasonKind,
    otherTabCount: otherTabCount ?? -1,
    otherTabCountLabel: tabCountLabel(otherTabCount),
  });
};

const trackRxDbDestructiveReset = (source: ResetSource) => {
  amplitudeTrackEvent('RxDB Destructive Reset', { source });
  amplitudeFlush();
};

const trackRxDbSchemaRebuildProblem = (
  reason: SchemaRebuildProblem['reason']
) => {
  amplitudeTrackEvent('RxDB Schema Rebuild Problem', { reason });
};

const captureRxDbPhaseTransition = (
  phaseStatus: string,
  detail?: Record<string, unknown>
) =>
  addSentryBreadcrumb({
    category: 'rxdb',
    message: `phase: ${phaseStatus}`,
    level: 'info',
    data: detail,
  });

const captureRxDbUnavailable = (
  reasonKind: string,
  otherTabCount: number | null,
  extra?: Record<string, unknown>
) =>
  captureSentryMessage({
    tags: [
      { key: 'rxdb.unavailable.reason', value: reasonKind },
      { key: 'rxdb.other_tab_count', value: tabCountLabel(otherTabCount) },
    ],
    level: 'error',
    message: `RxDB unavailable: ${reasonKind}`,
    context: {
      key: 'RxDB',
      detail: { extra, reason: reasonKind, otherTabCount },
    },
  });

export const setupRxDbTelemetry = (connection: RxDbConnectionService): void => {
  connection.status$
    .pipe(distinctUntilChanged((a, b) => a.phase.status === b.phase.status))
    .subscribe(({ phase, tabPresence }) => {
      captureRxDbPhaseTransition(phase.status, phaseDetail(phase));

      if (phase.status === 'unavailable' && phase.reason.kind !== 'disposed') {
        const otherTabCount = tabPresence?.otherTabCount ?? null;
        captureRxDbUnavailable(phase.reason.kind, otherTabCount, phase.reason);
        trackRxDbUnavailable(phase.reason.kind, otherTabCount);
      }

      if (
        phase.status === 'resetting' &&
        phase.trigger === 'manual-destructive'
      ) {
        trackRxDbDestructiveReset(phase.source);
      }
    });

  connection.status$
    .pipe(
      map(({ schemaRebuildProblem }) => schemaRebuildProblem),
      filter((problem) => problem !== null),
      distinctUntilChanged()
    )
    .subscribe((problem) => trackRxDbSchemaRebuildProblem(problem.reason));
};
